import { useTheme } from './theme';

// Deadline color coding: green → yellow → red based on days remaining
function getDeadlineColor(daysRemaining, warnThreshold = 5, criticalThreshold = 0) {
  if (daysRemaining < criticalThreshold) return '#FF6B6B'; // Red - overdue
  if (daysRemaining < warnThreshold) return '#FFA500'; // Orange - warning
  return '#4CAF50'; // Green - on track
}

function calculateDaysDiff(fromDate, toDate) {
  if (!fromDate || !toDate) return null;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  return Math.floor((to - from) / (1000 * 60 * 60 * 24));
}

function formatDays(days) {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  if (days < 0) return `${Math.abs(days)} days overdue`;
  return `${days} days`;
}

export function DeadlineDisplay({ alertGeneratedAt, regulatoryDueDate, internalSLADueDate, compact = false }) {
  const C = useTheme();

  const daysElapsed = calculateDaysDiff(alertGeneratedAt, new Date());
  const daysRemainingRegulatory = calculateDaysDiff(new Date(), regulatoryDueDate);
  const daysRemainingInternal = calculateDaysDiff(new Date(), internalSLADueDate);

  const effectiveDueDate = internalSLADueDate || regulatoryDueDate;
  const effectiveDaysRemaining = daysRemainingInternal !== null ? daysRemainingInternal : daysRemainingRegulatory;
  const deadlineColor = effectiveDaysRemaining !== null ? getDeadlineColor(effectiveDaysRemaining) : C.text;

  if (compact) {
    return (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.85rem' }}>
        {daysElapsed !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ color: C.steel, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>Elapsed</span>
            <span style={{ color: C.text, fontWeight: 600 }}>{formatDays(daysElapsed)}</span>
          </div>
        )}
        {effectiveDaysRemaining !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ color: C.steel, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>Remaining</span>
            <span style={{ color: deadlineColor, fontWeight: 700 }}>{formatDays(effectiveDaysRemaining)}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: `${deadlineColor}22`,
      border: `1px solid ${deadlineColor}44`,
      borderRadius: 8,
      padding: '1rem',
      marginBottom: '1rem'
    }}>
      <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
        Timeline & Deadlines
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.85rem' }}>
        {alertGeneratedAt && (
          <div>
            <div style={{ color: C.steel, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
              Alert Generated
            </div>
            <div style={{ color: C.text, fontWeight: 600 }}>{new Date(alertGeneratedAt).toLocaleDateString()}</div>
            <div style={{ color: C.steel, fontSize: '0.75rem' }}>({formatDays(daysElapsed)} ago)</div>
          </div>
        )}

        {internalSLADueDate && (
          <div>
            <div style={{ color: '#FFA500', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.25rem' }}>
              ⚡ Internal SLA Due
            </div>
            <div style={{ color: deadlineColor, fontWeight: 700 }}>{new Date(internalSLADueDate).toLocaleDateString()}</div>
            <div style={{ color: deadlineColor, fontSize: '0.75rem', fontWeight: 600 }}>{formatDays(daysRemainingInternal)}</div>
          </div>
        )}

        {regulatoryDueDate && !internalSLADueDate && (
          <div>
            <div style={{ color: C.steel, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
              Regulatory Due Date
            </div>
            <div style={{ color: C.text, fontWeight: 600 }}>{new Date(regulatoryDueDate).toLocaleDateString()}</div>
            <div style={{ color: deadlineColor, fontSize: '0.75rem', fontWeight: 600 }}>{formatDays(daysRemainingRegulatory)}</div>
          </div>
        )}

        {regulatoryDueDate && internalSLADueDate && (
          <div>
            <div style={{ color: C.steel, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
              Regulatory Deadline
            </div>
            <div style={{ color: C.text, fontWeight: 600 }}>{new Date(regulatoryDueDate).toLocaleDateString()}</div>
            <div style={{ color: C.steel, fontSize: '0.75rem' }}>({formatDays(daysRemainingRegulatory)} remaining)</div>
          </div>
        )}
      </div>

      {effectiveDaysRemaining !== null && (
        <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: deadlineColor + '33', borderRadius: 4 }}>
          <div style={{ color: deadlineColor, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>
            {effectiveDaysRemaining < 0 ? '⚠️ OVERDUE' : effectiveDaysRemaining < 5 ? '⚠️ DUE SOON' : '✓ ON TRACK'}
          </div>
        </div>
      )}
    </div>
  );
}

export function DeadlineTableCell({ alertGeneratedAt, regulatoryDueDate, internalSLADueDate }) {
  const C = useTheme();
  const daysElapsed = calculateDaysDiff(alertGeneratedAt, new Date());
  const daysRemaining = calculateDaysDiff(new Date(), internalSLADueDate || regulatoryDueDate);
  const color = daysRemaining !== null ? getDeadlineColor(daysRemaining) : C.text;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      padding: '0.4rem',
      background: color + '11',
      borderRadius: 4,
      borderLeft: `3px solid ${color}`
    }}>
      <div style={{ fontSize: '0.75rem', color: C.steel, fontWeight: 600 }}>
        {daysElapsed !== null ? `${daysElapsed}d elapsed` : '—'}
      </div>
      <div style={{ fontSize: '0.8rem', color: color, fontWeight: 700 }}>
        {daysRemaining !== null ? formatDays(daysRemaining) : '—'}
      </div>
    </div>
  );
}

export default DeadlineDisplay;
